import { createSignal, For, Show } from 'solid-js'
import lv1Closed from '~/assets/giftbox-lv1-closed.png'
import lv1Opened from '~/assets/giftbox-lv1-opened.png'
import lv2Closed from '~/assets/giftbox-lv2-closed.png'
import lv2Opened from '~/assets/giftbox-lv2-opened.png'
import lv3Closed from '~/assets/giftbox-lv3-closed.png'
import lv3Opened from '~/assets/giftbox-lv3-opened.png'
import tickBig from '~/assets/tick-big.svg'
import { Spinner } from '~/components/spinner'
import { useChestSounds } from '~/lib/audio/chest-sounds'
import { cn } from '~/lib/utils'

type Props = {
  level: 'lv1' | 'lv2' | 'lv3'
  state: 'locked' | 'ready' | 'claimed'
  loading?: boolean
  onClick: (event: MouseEvent) => void
}

const IMAGE_MAP: Record<Props['level'], { closed: string; opened: string }> = {
  lv1: { closed: lv1Closed, opened: lv1Opened },
  lv2: { closed: lv2Closed, opened: lv2Opened },
  lv3: { closed: lv3Closed, opened: lv3Opened },
}

const SPARKLES = [
  { x: -36, y: -28, size: 5, delay: 0 },
  { x: 34, y: -22, size: 4, delay: 0.4 },
  { x: -42, y: 18, size: 4, delay: 0.8 },
  { x: 38, y: 26, size: 5, delay: 1.1 },
  { x: 2, y: -44, size: 4, delay: 0.55 },
  { x: -18, y: 38, size: 4, delay: 0.25 },
]

const PARTICLES = [
  { tx: 80, ty: -38, size: 4, delay: 0 },
  { tx: 50, ty: -75, size: 3, delay: 0.15 },
  { tx: -68, ty: -58, size: 4, delay: 0.3 },
  { tx: -85, ty: 14, size: 3, delay: 0.45 },
  { tx: 90, ty: 26, size: 3, delay: 0.6 },
  { tx: 62, ty: 64, size: 4, delay: 0.9 },
  { tx: -46, ty: 78, size: 3, delay: 1.05 },
  { tx: 8, ty: -92, size: 3, delay: 1.2 },
  { tx: -14, ty: 88, size: 3, delay: 1.5 },
  { tx: 40, ty: -18, size: 3, delay: 0.75 },
]

const SIZE = 54

export function GiftBox(props: Props) {
  const [pressed, setPressed] = createSignal(false)
  const [opening, setOpening] = createSignal(false)
  const { playPress, playRelease } = useChestSounds()
  let pressArmed = false

  const isLoading = () => props.loading === true
  const isClaiming = () => isLoading() && props.state === 'ready'
  const isReady = () => !isLoading() && props.state === 'ready'
  const isLocked = () => isLoading() || props.state === 'locked'
  const isClaimed = () => !isLoading() && props.state === 'claimed'
  const showPress = () => pressed() && isReady()
  const images = () => IMAGE_MAP[props.level]
  const chestSrc = () => (isClaimed() ? images().opened : images().closed)

  const handleClick = (event: MouseEvent) => {
    if (!isReady()) return
    setOpening(true)
    props.onClick(event)
    window.setTimeout(() => setOpening(false), 600)
  }

  const handlePointerDown = () => {
    if (!isReady()) return
    setPressed(true)
    pressArmed = true
    playPress()
  }

  const handlePointerUp = () => {
    setPressed(false)
    if (!pressArmed) return
    pressArmed = false
    playRelease()
  }

  const handlePointerAbort = () => {
    setPressed(false)
    pressArmed = false
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerAbort}
      onPointerCancel={handlePointerAbort}
      disabled={!isReady()}
      aria-label={
        isClaimed()
          ? 'Reward claimed'
          : isReady()
            ? 'Claim reward'
            : 'Reward locked'
      }
      class={cn(
        'relative w-[54px] h-[54px] tap-highlight-none',
        // Only a ready chest is interactive. A locked/claimed (or loading)
        // chest lets taps fall through to the card body, which owns the
        // paywall entry for non-subscribers.
        isReady() ? 'cursor-pointer' : 'pointer-events-none cursor-default',
      )}
    >
      <Show when={isReady()}>
        <span
          aria-hidden="true"
          class="pointer-events-none absolute left-1/2 top-1/2 animate-halo-outer rounded-full"
          style={{
            width: `${SIZE * 2.6}px`,
            height: `${SIZE * 2.6}px`,
            background:
              'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 35%, rgba(255,255,255,0.55) 60%, rgba(255,255,255,0) 85%)',
            'box-shadow':
              '0 0 24px rgba(255,255,255,0.6), 0 0 12px rgba(255,255,255,0.5)',
            'animation-delay': '0.9s',
          }}
        />
        <span
          aria-hidden="true"
          class="pointer-events-none absolute left-1/2 top-1/2 animate-halo rounded-full"
          style={{
            width: `${SIZE * 2.0}px`,
            height: `${SIZE * 2.0}px`,
            background:
              'radial-gradient(circle, rgba(255,215,90,1) 0%, rgba(255,215,90,1) 30%, rgba(255,200,60,0.75) 60%, rgba(255,200,60,0) 85%)',
            'box-shadow':
              '0 0 32px rgba(255,170,40,0.7), 0 0 16px rgba(255,170,40,0.6)',
          }}
        />
        <For each={PARTICLES}>
          {(p) => (
            <span
              aria-hidden="true"
              class="pointer-events-none absolute left-1/2 top-1/2 animate-particle-fly"
              style={{
                width: `${p.size}px`,
                height: `${p.size}px`,
                'border-radius': '1.25rem',
                background: '#FFFFFF',
                'box-shadow': '0 0 4px 0 rgba(255, 209, 43, 0.64)',
                '--tx': `${p.tx}px`,
                '--ty': `${p.ty}px`,
                'animation-delay': `${p.delay}s`,
              }}
            />
          )}
        </For>
        <For each={SPARKLES}>
          {(s) => (
            <span
              aria-hidden="true"
              class="pointer-events-none absolute animate-sparkle-burst"
              style={{
                left: `calc(50% + ${s.x}px)`,
                top: `calc(50% + ${s.y}px)`,
                width: `${s.size}px`,
                height: `${s.size}px`,
                'margin-left': `${-s.size / 2}px`,
                'margin-top': `${-s.size / 2}px`,
                'animation-delay': `${s.delay}s`,
              }}
            >
              <Sparkle />
            </span>
          )}
        </For>
      </Show>

      <Show when={opening()}>
        <span
          aria-hidden="true"
          class="pointer-events-none absolute left-1/2 top-1/2 animate-burst rounded-full"
          style={{
            width: `${SIZE * 1.8}px`,
            height: `${SIZE * 1.8}px`,
            background:
              'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0.85) 30%, rgba(255,255,255,0) 65%)',
            'box-shadow':
              '0 0 40px rgba(255,255,255,0.8), 0 0 18px rgba(255,255,255,0.7)',
          }}
        />
      </Show>

      <span
        class={cn(
          'relative block h-full w-full',
          isReady() && !pressed() && !opening() && 'animate-giftbox-bob',
        )}
        style={{
          transition: 'transform 0.15s ease, filter 0.5s',
          transform: opening()
            ? 'scale(1.18) translateY(-6px)'
            : showPress()
              ? 'scale(0.94)'
              : undefined,
          filter: isLoading()
            ? 'brightness(0.75) contrast(1.5)'
            : isReady()
              ? 'drop-shadow(0 6px 12px rgba(220,150,60,0.3))'
              : undefined,
        }}
      >
        <img
          src={chestSrc()}
          alt=""
          draggable={false}
          class={cn(
            'absolute inset-0 h-full w-full object-contain pointer-events-none transition-opacity duration-300',
            isLocked() && 'opacity-95',
          )}
          style={{ opacity: opening() ? 0.75 : undefined }}
        />

        <span
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 transition-opacity duration-150"
          style={{
            opacity: showPress() ? 0.55 : 0,
            background:
              'linear-gradient(135deg, rgba(255,255,255,0) 25%, rgba(255,255,255,0.35) 48%, rgba(255,255,255,0.5) 52%, rgba(255,255,255,0) 78%)',
            'mix-blend-mode': 'overlay',
            'mask-image': `url(${chestSrc()})`,
            'mask-size': 'contain',
            'mask-repeat': 'no-repeat',
            'mask-position': 'center',
            '-webkit-mask-image': `url(${chestSrc()})`,
            '-webkit-mask-size': 'contain',
            '-webkit-mask-repeat': 'no-repeat',
            '-webkit-mask-position': 'center',
          }}
        />
        <span
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 transition-opacity duration-150"
          style={{
            opacity: showPress() ? 0.22 : 0,
            background: 'rgba(255,255,255,1)',
            'mix-blend-mode': 'soft-light',
            'mask-image': `url(${chestSrc()})`,
            'mask-size': 'contain',
            'mask-repeat': 'no-repeat',
            'mask-position': 'center',
            '-webkit-mask-image': `url(${chestSrc()})`,
            '-webkit-mask-size': 'contain',
            '-webkit-mask-repeat': 'no-repeat',
            '-webkit-mask-position': 'center',
          }}
        />
      </span>

      <Show when={isClaimed()}>
        <img
          src={tickBig}
          alt=""
          aria-hidden="true"
          draggable={false}
          class="pointer-events-none absolute bottom-[-2px] right-[-4px] z-10 h-[26px] w-[30px] drop-shadow-[0_2px_6px_rgba(141,201,37,0.6)]"
        />
      </Show>

      <Show when={isClaiming()}>
        <span class="pointer-events-none absolute inset-0 grid place-items-center">
          <Spinner class="size-[14px] text-white" variant="circle" />
        </span>
      </Show>
    </button>
  )
}

function Sparkle() {
  return (
    <svg viewBox="0 0 24 24" class="h-full w-full" aria-hidden="true">
      <defs>
        <radialGradient id="giftbox-sparkle-grad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#fffdf2" stop-opacity="1" />
          <stop offset="55%" stop-color="#ffe6a8" stop-opacity="0.85" />
          <stop offset="100%" stop-color="#ffcc70" stop-opacity="0" />
        </radialGradient>
      </defs>
      <path
        d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z"
        fill="url(#giftbox-sparkle-grad)"
      />
    </svg>
  )
}
